import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    TextField,
    MenuItem,
    Alert,
    Snackbar,
    Stack,
    Chip,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import ECNsList from "./ECNsList";
import ECNFormDialog from "./ECNFormDialog";
import ECNViewDialog from "./ECNViewDialog";
import DeleteECNDialog from "./DeleteECNDialog";
import EngineeringService from "../../../services/Engineering.service";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function ECNs() {
    const [year, setYear] = useState(currentYear);
    const [search, setSearch] = useState("");
    const [ecns, setEcns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [nextNumber, setNextNumber] = useState("");

    const [formDialog, setFormDialog] = useState({ open: false, item: null });
    const [viewDialog, setViewDialog] = useState({ open: false, item: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const refresh = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await EngineeringService.listECNs({ year });
            setEcns(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError("Failed to load ECNs.");
        } finally {
            setLoading(false);
        }
    }, [year]);

    const refreshNextNumber = useCallback(() => {
        EngineeringService.nextECNNumber()
            .then((res) => setNextNumber(res.data.next_number))
            .catch(() => setNextNumber(""));
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        refreshNextNumber();
    }, [refreshNextNumber]);

    const handleSaved = () => {
        refresh();
        refreshNextNumber();
        showSnackbar(formDialog.item ? "ECN updated." : "ECN created.");
    };

    const handleDeleted = () => {
        refresh();
        showSnackbar("ECN deleted.", "info");
    };

    const filteredEcns = ecns.filter((ecn) => {
        const term = search.trim().toLowerCase();
        if (!term) return true;
        return (
            ecn.number?.toLowerCase().includes(term) ||
            ecn.title?.toLowerCase().includes(term)
        );
    });

    const externalCount = filteredEcns.filter((ecn) => ecn.is_external).length;
    const internalCount = filteredEcns.length - externalCount;

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <TextField
                    select
                    label="Year"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 120 }, flexShrink: 0 }}
                >
                    {YEAR_OPTIONS.map((y) => (
                        <MenuItem key={y} value={y}>
                            {y}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Search"
                    placeholder="Number or title"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormDialog({ open: true, item: null })}
                    sx={{ flexShrink: 0, height: 40 }}
                >
                    New ECN{nextNumber ? ` ${nextNumber}` : ""}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`${filteredEcns.length} Total`} size="small" variant="outlined" />
                <Chip label={`${externalCount} External`} size="small" color="secondary" />
                <Chip label={`${internalCount} Internal`} size="small" />
            </Stack>

            <ECNsList
                ecns={filteredEcns}
                loading={loading}
                onView={(ecn) => setViewDialog({ open: true, item: ecn })}
                onEdit={(ecn) => setFormDialog({ open: true, item: ecn })}
                onDelete={(ecn) => setDeleteDialog({ open: true, item: ecn })}
            />

            <ECNFormDialog
                open={formDialog.open}
                onClose={() => setFormDialog({ open: false, item: null })}
                item={formDialog.item}
                onSaved={handleSaved}
            />

            <ECNViewDialog
                open={viewDialog.open}
                onClose={() => setViewDialog({ open: false, item: null })}
                ecn={viewDialog.item}
            />

            <DeleteECNDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, item: null })}
                ecn={deleteDialog.item}
                onDeleted={handleDeleted}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
